import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  Box,
  Grid,
  CircularProgress,
  Backdrop,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { AddData } from "src/app-redux/common-actions";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import AlertDialog from "src/components/AlertDialog/AlertDialog";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButtonOutLined from "src/components/CommonButton/CommonButtonOutLined";
import CommonButtonWithOutBor from "src/components/CommonButton/CommonButtonWOutBorder";
import CommonXGrid from "src/components/CommonXGrid/CommonXGrid";
import Heading from "./Heading";
import { useStyles } from "./styles";

type Props = {
  expand: boolean;
  updateExpandStatus: (type: number) => void;
};

export default function Trades({ expand, updateExpandStatus }: Props): React.ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [loader, setLoader] = useState(false);
  const [tradesData, setTradesData] = useState([]);
  const [tradesType, setTradesType] = React.useState<any[]>();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [tradeId, setTradeId] = useState<number>(0);
  const [seltrades, setSeltrades] = useState<string>("");

  const getData = async () => {
    try {
      setLoader(true);
      const results = await fetchWrapper.get("get-vendor-trade-by-vendor-id");
      if (results?.vendorTradeByVendorIds) {
        setTradesData(results.vendorTradeByVendorIds);
      }
      return setLoader(false);
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
  };
  async function getVendorTrades() {
    setLoader(true);
    if (!tradesType?.length) {
      const { vendorTrades } = await fetchWrapper.get("/get-vendor-trades");
      if (vendorTrades) {
        const res: any[] = [];
        if (vendorTrades?.length > 0)
          vendorTrades.map((v: any) => {
            const obj: any = {
              label: v,
              value: v,
            };
            res.push(obj);
          });
        setTradesType(res);
        setLoader(false);
      }
    }
    setLoader(false);
  }
  const deleteRecord = async () => {
    setLoader(true);
    try {
      setLoader(true);
      const res = await fetchWrapper.delete(`delete-vendor-trade/${tradeId}`);
      if (res.success) {
        dispatch(
          addNotificationAction({
            message: "Vendor Trade deleted successfully!",
            options: { variant: "success" },
          })
        );
        getData();
      } else {
        throw res.error;
      }
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
    setIsConfirmDialogOpen(false);
    setLoader(false);
    setTradeId(0);
    getData();
  };
  const columns: GridColDef[] = [
    {
      field: "tradeCode",
      headerName: "Trade Name",
      type: "string",
      width: 300,
      aggregable: false,
    },
    {
      field: "id",
      headerName: "Action",
      type: "string",
      width: 350,
      filterable: false,
      sortable: false,
      aggregable: false,
      groupable: false,
      renderCell: function customCell(params) {
        return (
          <CommonButtonWithOutBor
            onClick={() => {
              setTradeId(params.row.id);
              setIsConfirmDialogOpen(true);
            }}
          >
            Delete
          </CommonButtonWithOutBor>
        );
      },
    },
  ];
  const submitForm = async () => {
    setLoader(true);
    const body = {
      code: seltrades,
    };
    try {
      await dispatch(
        AddData("/create-vendor-trade", "Vendor trade added successfully", JSON.stringify(body))
      );
      getData();
    } catch (error) {
      setLoader(false);
    }
    setLoader(false);
    setSeltrades("");
  };
  useEffect(() => {
    if (expand) {
      setSeltrades("");
      getData();
      getVendorTrades();
    }
  }, [expand]);
  return (
    <>
      <Backdrop className={classes.backdrop} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box border={1} mb={2} borderColor="grey.200">
        <Accordion square elevation={0} expanded={expand} onChange={() => updateExpandStatus(1)}>
          <Heading heading="Trades" />
          <Box borderTop={1} mb={1} borderColor="grey.200"></Box>
          <AccordionDetails>
            <Grid container direction="row">
              <Grid item xs={12} md={3}>
                <CommonAutoCompleteField
                  label="Trades"
                  name="trades"
                  required
                  optionsType={tradesType}
                  getOptionLabel={(option: any) => option.label || ""}
                  value={tradesType?.find((ty: any) => ty.value === seltrades) || null}
                  onChange={(option: any) => setSeltrades(option?.value || "")}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box component="span" textAlign={"right"} ml={4}>
                  <CommonButtonOutLined
                    isGreen
                    disable={!seltrades}
                    onClick={() => {
                      submitForm();
                    }}
                  >
                    Add New Trade
                  </CommonButtonOutLined>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box color="text.secondary" mt={2}>
                  <Typography className={classes.heading}>Trade List</Typography>
                </Box>
                <CommonXGrid
                  rows={tradesData}
                  columns={columns}
                  defaultPerPageRecord={1000}
                  height={450}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        handleClose={() => {
          setIsConfirmDialogOpen(false);
        }}
        message={"Are you sure you want to Delete this Trade?"}
        primaryButtonText="Ok"
        onPrimaryButtonClick={() => {
          deleteRecord();
        }}
        heading={"Delete Trade"}
      />
    </>
  );
}
